/**
 * Dominance decision for the in-room layout — Phase 1B-H-multi (UX).
 *
 * Picks WHICH track should occupy the large "dominant" tile and which
 * become thumbnails. Pure, no LiveKit imports — the caller hands us
 * the already-fetched track refs + a few flags and we return the
 * split. Lives in a separate module so it's unit-testable without
 * mocking the entire LiveKit component tree.
 *
 * Hierarchy (top wins):
 *   1. Active screen share (anyone). Field + operator both need to
 *      see what's being demonstrated at full size.
 *   2. **Operator-promoted tile** — when the operator clicks a
 *      thumbnail, that identity becomes dominant until they unpin.
 *      Only applies when the local participant is the operator;
 *      field-side ignores the promotion (they always see the same
 *      hierarchy as before).
 *   3. Field's camera (the inspection's annotation surface).
 *   4. Local participant's camera (fallback before the field joins).
 *
 * The "others" list is everything left (placeholders dropped so a
 * remote who hasn't published yet doesn't render as a ghost tile).
 */

import {
  isTrackReference,
  type TrackReferenceOrPlaceholder,
} from "@livekit/components-core";

export const FIELD_IDENTITY_PREFIX = "participant:";

export type DominanceResult = {
  dominant: TrackReferenceOrPlaceholder | null;
  others: TrackReferenceOrPlaceholder[];
};

/** Identifier helpers — exported so the click-handlers can derive the
 * same identity string the dominance function compares against. */
export function identityOf(track: TrackReferenceOrPlaceholder): string {
  return track.participant.identity ?? "";
}

export function isFieldIdentity(identity: string): boolean {
  return identity.startsWith(FIELD_IDENTITY_PREFIX);
}

export function pickDominantTrack({
  cameraTracks,
  screenShareTracks,
  promotedIdentity,
  isOperator,
}: {
  cameraTracks: TrackReferenceOrPlaceholder[];
  screenShareTracks: TrackReferenceOrPlaceholder[];
  promotedIdentity: string | null;
  isOperator: boolean;
}): DominanceResult {
  // 1) Active screen share wins.
  const screenShare = screenShareTracks.find(isTrackReference) ?? null;
  if (screenShare) {
    return {
      dominant: screenShare,
      others: cameraTracks.filter(isTrackReference),
    };
  }

  // 2) Operator-promoted tile (only honoured when the local view IS the
  // operator's). Find the camera track whose participant identity
  // matches. Skip when the promoted participant isn't currently in the
  // room (covers the case where they disconnected after being pinned).
  if (isOperator && promotedIdentity) {
    const promoted = cameraTracks.find(
      (t) => identityOf(t) === promotedIdentity && isTrackReference(t),
    );
    if (promoted) {
      return {
        dominant: promoted,
        others: cameraTracks
          .filter((t) => t !== promoted)
          .filter(isTrackReference),
      };
    }
  }

  // 3) Field's camera.
  const fieldTrack =
    cameraTracks.find((t) => isFieldIdentity(identityOf(t))) ?? null;

  // 4) Local fallback.
  const localTrack = cameraTracks.find((t) => t.participant.isLocal) ?? null;

  const dom: TrackReferenceOrPlaceholder | null = fieldTrack ?? localTrack;
  const rest = (dom
    ? cameraTracks.filter((t) => t !== dom)
    : cameraTracks
  ).filter(isTrackReference);
  return { dominant: dom, others: rest };
}
